/**
 * useUserPrompts
 * Manages the current user's "My Prompts" list (fetch, add, delete).
 */
import { useState, useEffect, useCallback } from 'react';
import OrbitUserPromptsService from '../services/OrbitUserPromptsService';
export interface UserPrompt {
  id: number;
  label: string;
  prompt_text?: string | null;
  client_id: number | null;
}

export interface SaveUserPromptInput {
  label: string;
  promptText?: string;
}
interface UseUserPromptsResult {
  prompts:  UserPrompt[];
  loading:  boolean;
  addPrompt:    (input: string | SaveUserPromptInput) => Promise<void>;
  deletePrompt: (id: number)   => Promise<void>;
}
export function useUserPrompts(clientId?: number): UseUserPromptsResult {
  const [prompts, setPrompts] = useState<UserPrompt[]>([]);
  const [loading, setLoading] = useState(true);
  const load = useCallback(async () => {
    try {
      const res = await OrbitUserPromptsService.list(
        clientId && clientId > 0 ? { client_id: clientId } : {}
      );
      setPrompts(res?.data ?? []);
    } catch {
      // Silently fail — empty list is acceptable
    } finally {
      setLoading(false);
    }
  }, [clientId]);
  useEffect(() => { load(); }, [load]);
  const addPrompt = useCallback(async (input: string | SaveUserPromptInput) => {
    const normalized = typeof input === 'string'
      ? { label: input, promptText: input }
      : input;
    const payload: Record<string, any> = {
      label: normalized.label,
      prompt_text: normalized.promptText ?? normalized.label,
    };
    if (clientId && clientId > 0) payload.client_id = clientId;
    await OrbitUserPromptsService.create(payload);
    await load();
  }, [clientId, load]);
  const deletePrompt = useCallback(async (id: number) => {
    await OrbitUserPromptsService.delete(id);
    setPrompts((prev) => prev.filter((p) => p.id !== id));
  }, []);
  return { prompts, loading, addPrompt, deletePrompt };
}
